import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  FileText,
  Loader2,
  Plus,
  Edit,
  Trash2,
  Palette,
  Settings,
  Check,
  Image,
  Upload,
  X,
  Star,
  CheckSquare,
  Square
} from "lucide-react";

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  
  // Multi-selection state
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deletingMultiple, setDeletingMultiple] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    header_text: "Informe de Comparación de Pólizas",
    footer_text: "© QAsesores - Corredores de Seguros y Reaseguros",
    logo_url: "",
    is_default: false,
    include_coverages: true,
    include_differences: true,
    include_validity: true,
    include_recommendations: true,
    custom_recommendations: "",
    primary_color: "#0C314C",
    accent_color: "#45E69A"
  });
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/templates`, getAuthHeaders());
      setTemplates(res.data);
    } catch (err) {
      toast.error("Error al cargar las plantillas");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      header_text: "Informe de Comparación de Pólizas",
      footer_text: "© QAsesores - Corredores de Seguros y Reaseguros",
      logo_url: "",
      is_default: false,
      include_coverages: true,
      include_differences: true,
      include_validity: true,
      include_recommendations: true,
      custom_recommendations: "",
      primary_color: "#0C314C",
      accent_color: "#45E69A"
    });
    setEditingTemplate(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  // Multi-selection functions
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedItems(new Set());
  };

  const toggleItemSelection = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
  };

  const selectAllItems = () => {
    const allIds = new Set(templates.map(t => t.id));
    setSelectedItems(allIds);
  };

  const deselectAllItems = () => {
    setSelectedItems(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return;
    setShowDeleteConfirmDialog(true);
  };

  const confirmDeleteSelected = async () => {
    setDeletingMultiple(true);
    let successCount = 0;
    let errorCount = 0;

    for (const id of selectedItems) {
      try {
        await axios.delete(`${API}/templates/${id}`, getAuthHeaders());
        successCount++;
      } catch (error) {
        console.error(`Error deleting template ${id}:`, error);
        errorCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} plantilla(s) eliminada(s) exitosamente`);
      fetchTemplates();
    }
    if (errorCount > 0) {
      toast.error(`No se pudieron eliminar ${errorCount} plantilla(s)`);
    }

    setSelectedItems(new Set());
    setIsSelectionMode(false);
    setDeletingMultiple(false);
    setShowDeleteConfirmDialog(false);
  };

  // Helper function to convert external URL to base64
  const convertLogoToBase64 = async (url) => {
    if (!url || url.startsWith('data:')) return url; // Already base64 or empty
    
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.log("Could not convert logo to base64:", err.message);
      return null; // Signal that conversion failed
    }
  };

  const openEditDialog = async (template) => {
    setEditingTemplate(template);
    
    let logoUrl = template.logo_url || "";
    
    // If logo is an external URL (not base64), try to convert it
    if (logoUrl && !logoUrl.startsWith('data:')) {
      const convertedLogo = await convertLogoToBase64(logoUrl);
      if (convertedLogo) {
        logoUrl = convertedLogo;
        toast.success("Logo convertido correctamente");
      } else {
        // Show warning and clear the logo - user needs to re-upload
        toast.warning("El logo necesita ser re-subido para funcionar correctamente en PDFs", {
          duration: 5000
        });
        logoUrl = ""; // Clear it so user knows to re-upload
      }
    }
    
    setFormData({
      name: template.name,
      description: template.description || "",
      header_text: template.header_text || "Informe de Comparación de Pólizas",
      footer_text: template.footer_text || "© QAsesores - Corredores de Seguros y Reaseguros",
      logo_url: logoUrl,
      is_default: template.is_default || false,
      include_coverages: template.include_coverages,
      include_differences: template.include_differences,
      include_validity: template.include_validity,
      include_recommendations: template.include_recommendations,
      custom_recommendations: template.custom_recommendations || "",
      primary_color: template.primary_color || "#0C314C",
      accent_color: template.accent_color || "#45E69A"
    });
    setShowDialog(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Solo se permiten archivos de imagen");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El archivo debe ser menor a 2MB");
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData({ ...formData, logo_url: event.target.result });
        setUploadingLogo(false);
        toast.success("Logo cargado exitosamente");
      };
      reader.onerror = () => {
        toast.error("Error al cargar el logo");
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Error al cargar el logo");
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    setFormData({ ...formData, logo_url: "" });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, formData, getAuthHeaders());
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/templates`, formData, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchTemplates();
    } catch (err) {
      toast.error("Error al guardar la plantilla");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/templates/${id}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      setTemplates(templates.filter(t => t.id !== id));
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="templates-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Plantillas de Informes
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Personaliza la apariencia y contenido de tus informes
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Multi-selection controls */}
            {isSelectionMode ? (
              <>
                <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-lg">
                  <span className="text-sm text-slate-600 dark:text-slate-300">
                    {selectedItems.size} seleccionados
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="select-all-templates-button"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Todos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={deselectAllItems}
                  className="text-slate-600 dark:text-slate-300"
                  data-testid="deselect-all-templates-button"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Ninguno
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={selectedItems.size === 0 || deletingMultiple}
                  data-testid="delete-selected-templates-button"
                >
                  {deletingMultiple ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Eliminar ({selectedItems.size})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectionMode}
                  data-testid="cancel-selection-templates-button"
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                {templates.length > 0 && (
                  <Button 
                    variant="outline"
                    onClick={toggleSelectionMode}
                    className="text-slate-600 dark:text-slate-300"
                    data-testid="enable-selection-templates-button"
                  >
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Seleccionar
                  </Button>
                )}
                <Button 
                  onClick={openNewDialog}
                  className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  data-testid="new-template-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Plantilla
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : templates.length === 0 ? (
          <Card className="border-slate-200 dark:border-slate-700 shadow-sm dark:bg-slate-800">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400 mb-4">No tienes plantillas creadas</p>
              <Button onClick={openNewDialog} variant="outline" className="border-slate-200 dark:border-slate-600 dark:text-slate-300">
                <Plus className="w-4 h-4 mr-2" />
                Crear primera plantilla
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <Card 
                key={template.id} 
                className={`border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md dark:bg-slate-800 transition-shadow relative ${
                  isSelectionMode && selectedItems.has(template.id) ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                }`}
                data-testid={`template-card-${template.id}`}
              >
                {/* Selection checkbox */}
                {isSelectionMode && (
                  <div className="absolute top-3 left-3 z-10">
                    <Checkbox
                      checked={selectedItems.has(template.id)}
                      onCheckedChange={() => toggleItemSelection(template.id)}
                      className="w-5 h-5 bg-white dark:bg-slate-700 border-2"
                      data-testid={`checkbox-template-${template.id}`}
                    />
                  </div>
                )}
                <CardHeader className={`pb-3 ${isSelectionMode ? 'pl-12' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {template.logo_url ? (
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                          <img src={template.logo_url} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: template.primary_color }}
                        >
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                      )}
                      <div>
                        <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                          {template.name}
                          {template.is_default && (
                            <span className="inline-flex items-center gap-1 text-xs bg-[#45E69A] text-[#0C314C] px-2 py-0.5 rounded-full font-medium">
                              <Star className="w-3 h-3" />
                              Predeterminada
                            </span>
                          )}
                        </CardTitle>
                        <p className="text-xs text-slate-500">
                          Creada {formatDate(template.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {template.description && (
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                  
                  {/* Logo Preview */}
                  {template.logo_url && (
                    <div className="mb-3 flex items-center gap-2 text-xs text-slate-500">
                      <Image className="w-3 h-3" />
                      Logo personalizado incluido
                    </div>
                  )}
                  
                  {/* Color Preview */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Palette className="w-3 h-3" />
                      Colores:
                    </div>
                    <div 
                      className="w-6 h-6 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: template.primary_color }}
                      title="Color primario"
                    />
                    <div 
                      className="w-6 h-6 rounded-full border-2 border-white shadow"
                      style={{ backgroundColor: template.accent_color }}
                      title="Color de acento"
                    />
                  </div>
                  
                  {/* Sections included */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {template.include_coverages && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Coberturas</span>
                    )}
                    {template.include_differences && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Diferencias</span>
                    )}
                    {template.include_validity && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Vigencias</span>
                    )}
                    {template.include_recommendations && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Recomendaciones</span>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditDialog(template)}
                      className="flex-1 border-slate-200"
                      data-testid={`edit-template-${template.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteTemplate(template.id)}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`delete-template-${template.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold font-['Manrope']">
                {editingTemplate ? "Editar Plantilla" : "Nueva Plantilla"}
              </DialogTitle>
              <DialogDescription>
                Configura los ajustes de tu plantilla de informes
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la plantilla *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ej: Informe Ejecutivo Premium"
                    data-testid="template-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Descripción breve de la plantilla..."
                    rows={2}
                  />
                </div>

                {/* Set as Default Toggle */}
                <div className="flex items-center justify-between p-4 bg-[#45E69A]/10 rounded-lg border border-[#45E69A]/30">
                  <div className="flex items-center gap-3">
                    <Star className="w-5 h-5 text-[#0C314C]" />
                    <div>
                      <Label htmlFor="is_default" className="cursor-pointer text-[#0C314C] font-medium">
                        Establecer como predeterminada
                      </Label>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Esta plantilla se usará por defecto en nuevos informes
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="is_default"
                    checked={formData.is_default}
                    onCheckedChange={(checked) => setFormData({...formData, is_default: checked})}
                    data-testid="template-default-toggle"
                  />
                </div>
              </div>

              {/* Logo Upload */}
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Logo del Informe
                </h4>
                <p className="text-sm text-slate-500">
                  Este logo aparecerá en la parte superior central de cada página del informe
                </p>
                
                {formData.logo_url ? (
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-20 border border-slate-200 rounded-lg overflow-hidden bg-slate-50 flex items-center justify-center">
                      <img 
                        src={formData.logo_url} 
                        alt="Logo preview" 
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => logoInputRef.current?.click()}
                        className="border-slate-200"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Cambiar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={removeLogo}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 transition-colors"
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {uploadingLogo ? (
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto mb-2" />
                    ) : (
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    )}
                    <p className="text-sm text-slate-600">
                      Haz clic para subir un logo
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      PNG, JPG o GIF (máx. 2MB)
                    </p>
                  </div>
                )}
                
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>

              {/* Header & Footer */}
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Encabezado y Pie
                </h4>
                
                <div className="space-y-2">
                  <Label htmlFor="header">Texto del encabezado</Label>
                  <Input
                    id="header"
                    value={formData.header_text}
                    onChange={(e) => setFormData({...formData, header_text: e.target.value})}
                    placeholder="Informe de Comparación de Pólizas"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="footer">Texto del pie de página</Label>
                  <Input
                    id="footer"
                    value={formData.footer_text}
                    onChange={(e) => setFormData({...formData, footer_text: e.target.value})}
                    placeholder="© QAsesores - Corredores de Seguros y Reaseguros"
                  />
                </div>
              </div>

              {/* Colors */}
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Colores
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="primary_color">Color primario</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="primary_color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={(e) => setFormData({...formData, primary_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="accent_color">Color de acento</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        id="accent_color"
                        value={formData.accent_color}
                        onChange={(e) => setFormData({...formData, accent_color: e.target.value})}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={formData.accent_color}
                        onChange={(e) => setFormData({...formData, accent_color: e.target.value})}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Sections */}
              <div className="space-y-4 border-t border-slate-200 pt-4">
                <h4 className="font-medium text-slate-900">Secciones a incluir</h4>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="include_coverages" className="cursor-pointer">Coberturas</Label>
                    <Switch
                      id="include_coverages"
                      checked={formData.include_coverages}
                      onCheckedChange={(checked) => setFormData({...formData, include_coverages: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="include_differences" className="cursor-pointer">Diferencias</Label>
                    <Switch
                      id="include_differences"
                      checked={formData.include_differences}
                      onCheckedChange={(checked) => setFormData({...formData, include_differences: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="include_validity" className="cursor-pointer">Vigencias</Label>
                    <Switch
                      id="include_validity"
                      checked={formData.include_validity}
                      onCheckedChange={(checked) => setFormData({...formData, include_validity: checked})}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Label htmlFor="include_recommendations" className="cursor-pointer">Recomendaciones</Label>
                    <Switch
                      id="include_recommendations"
                      checked={formData.include_recommendations}
                      onCheckedChange={(checked) => setFormData({...formData, include_recommendations: checked})}
                    />
                  </div>
                </div>
              </div>

              {/* Custom Recommendations */}
              {formData.include_recommendations && (
                <div className="space-y-2">
                  <Label htmlFor="custom_recommendations">Recomendaciones personalizadas (opcional)</Label>
                  <Textarea
                    id="custom_recommendations"
                    value={formData.custom_recommendations}
                    onChange={(e) => setFormData({...formData, custom_recommendations: e.target.value})}
                    placeholder="Deja vacío para usar las recomendaciones por defecto..."
                    rows={3}
                  />
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                data-testid="save-template-button"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirmDialog} onOpenChange={setShowDeleteConfirmDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-white">
                Confirmar eliminación
              </DialogTitle>
              <DialogDescription>
                ¿Estás seguro de que deseas eliminar {selectedItems.size} plantilla(s)? 
                Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirmDialog(false)}
                disabled={deletingMultiple}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteSelected}
                disabled={deletingMultiple}
                data-testid="confirm-delete-templates-button"
              >
                {deletingMultiple ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar {selectedItems.size}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
